import React from 'react';
import { Link } from 'react-router-dom';
import { ChartLineUp, BracketsAngle, TrendUp } from '@phosphor-icons/react';
import { useLanguage } from '../context/LanguageContext';
import { LanguageSelector } from '../components/LanguageSelector';

export default function Landing() {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-background grain-overlay">
      {/* Hero Section */}
      <div className="relative min-h-screen flex flex-col">
        {/* Background Image Overlay */}
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1676746424139-77f8bd8922a8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHw0fHxzb2NjZXIlMjBzdGFkaXVtJTIwbGlnaHRzfGVufDB8fHx8MTc3NDEzNTU5MHww&ixlib=rb-4.1.0&q=85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/50 to-background" />
        </div>

        {/* Navbar */}
        <nav className="relative z-10 border-b border-white/5 backdrop-blur-md bg-background/30">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 py-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="SharpBetAI" className="h-16 w-auto" />
            </div>
            <div className="flex items-center gap-4">
              <LanguageSelector />
              <Link 
                to="/login" 
                className="px-6 py-2.5 text-sm font-medium text-white/80 hover:text-white transition-colors duration-150"
                data-testid="nav-login-link"
              >
                {t('login')}
              </Link>
              <Link 
                to="/signup" 
                className="px-6 py-2.5 bg-primary text-background font-bold rounded-full hover:bg-primary/90 transition-all duration-150 shadow-lg hover:shadow-primary/20"
                data-testid="nav-signup-link"
              >
                {t('startFree')}
              </Link>
            </div>
          </div>
        </nav>

        {/* Hero Content */}
        <div className="relative z-10 flex-1 flex items-center">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
            <div className="max-w-4xl">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/30 bg-primary/5 mb-8">
                <BracketsAngle weight="bold" className="text-primary text-sm" />
                <span className="text-primary font-mono text-sm font-semibold">{t('poweredByAI')}</span>
              </div>
              
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-heading font-black text-white mb-6 leading-tight">
                {t('heroTitle')}<br />
                <span className="text-primary">{t('heroTitleHighlight')}</span>
              </h1>
              
              <p className="text-xl text-white/60 mb-12 max-w-2xl leading-relaxed">
                {t('heroSubtitle')}
              </p>
              
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <Link 
                  to="/signup" 
                  className="px-10 py-4 bg-primary text-background font-black text-lg rounded-full hover:bg-primary/90 transition-all duration-150 shadow-xl hover:shadow-primary/30 hover:scale-105"
                  data-testid="hero-cta-button"
                >
                  {t('generateFirstSlip')}
                </Link>
                <Link 
                  to="/login" 
                  className="px-10 py-4 border-2 border-white/20 text-white font-bold text-lg rounded-full hover:border-white/40 hover:bg-white/5 transition-all duration-150"
                  data-testid="hero-login-button"
                >
                  {t('alreadyHaveAccount')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="relative z-10 py-32 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-4xl sm:text-5xl font-heading font-black text-white mb-4">{t('howItWorks')}</h2>
            <p className="text-lg text-white/50">{t('advancedTech')}</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="glass-surface p-8 rounded-sm group hover:border-primary/50 transition-all duration-200">
              <div className="w-14 h-14 bg-primary/10 rounded-sm flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors duration-200">
                <ChartLineUp weight="duotone" className="text-primary text-3xl" />
              </div>
              <h3 className="text-xl font-heading font-bold text-white mb-3">{t('feature1Title')}</h3>
              <p className="text-white/60 leading-relaxed">
                {t('feature1Desc')}
              </p>
            </div>

            <div className="glass-surface p-8 rounded-sm group hover:border-secondary/50 transition-all duration-200">
              <div className="w-14 h-14 bg-secondary/10 rounded-sm flex items-center justify-center mb-6 group-hover:bg-secondary/20 transition-colors duration-200">
                <BracketsAngle weight="duotone" className="text-secondary text-3xl" />
              </div>
              <h3 className="text-xl font-heading font-bold text-white mb-3">{t('feature2Title')}</h3>
              <p className="text-white/60 leading-relaxed">
                {t('feature2Desc')}
              </p>
            </div>

            <div className="glass-surface p-8 rounded-sm group hover:border-risk-low/50 transition-all duration-200">
              <div className="w-14 h-14 bg-risk-low/10 rounded-sm flex items-center justify-center mb-6 group-hover:bg-risk-low/20 transition-colors duration-200">
                <TrendUp weight="duotone" className="text-risk-low text-3xl" />
              </div>
              <h3 className="text-xl font-heading font-bold text-white mb-3">{t('feature3Title')}</h3>
              <p className="text-white/60 leading-relaxed">
                {t('feature3Desc')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="relative z-10 py-32 px-6 lg:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <div className="glass-surface p-12 lg:p-16 rounded-sm border-primary/20">
            <h2 className="text-4xl sm:text-5xl font-heading font-black text-white mb-6">
              {t('readyToStart')}
            </h2>
            <p className="text-xl text-white/60 mb-10 max-w-2xl mx-auto">
              {t('ctaSubtitle')}
            </p>
            <Link 
              to="/signup" 
              className="inline-block px-12 py-5 bg-primary text-background font-black text-xl rounded-full hover:bg-primary/90 transition-all duration-150 shadow-2xl hover:shadow-primary/30 hover:scale-105"
              data-testid="cta-signup-button"
            >
              {t('startNow')}
            </Link>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/5 py-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-white/40 text-sm">{t('footerCopyright')}</p>
            <div className="flex items-center gap-6">
              <Link to="/terms" className="text-white/40 text-sm hover:text-white transition-colors">
                {t('termsOfService')}
              </Link>
              <Link to="/privacy" className="text-white/40 text-sm hover:text-white transition-colors">
                {t('privacyPolicy')}
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
